import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { 
  Truck, 
  Wrench, 
  Clock, 
  Shield, 
  Users, 
  Award,
  Phone,
  Mail,
  MapPin,
  Settings,
  Hammer,
  Building,
  ShoppingCart,
  HardHat,
  Cog
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import SEOMeta from "@/components/SEOMeta";

// Import images
import heavyEquipmentSite from "@/assets/heavy-equipment-site.jpg";
import heavyEquipment from "@/assets/heavy-equipment.jpg";
import excavatorOntario from "@/assets/excavator-ontario.jpg";
import heavyEquipmentOperator from "@/assets/heavy-equipment-operator.jpg";
import heavyEquipmentRental from "@/assets/heavy-equipment-rental.jpg";
import heavyEquipmentDemolition from "@/assets/heavy-equipment-demolition.jpg";
import heavyEquipmentMaintenance from "@/assets/heavy-equipment-maintenance.jpg";

const HeavyEquipmentServices = () => {
  const faqData = [
    {
      question: "What types of heavy equipment do you provide?",
      answer: "We provide a comprehensive fleet including excavators, bulldozers, cranes, loaders, graders, compactors, dump trucks, and specialized construction equipment for all project sizes."
    },
    {
      question: "Do you offer equipment rental or purchase options?",
      answer: "We offer both short-term and long-term equipment rental options, as well as equipment sales and lease-to-own programs to meet your specific project needs and budget."
    },
    {
      question: "Are your heavy equipment operators certified?",
      answer: "Yes, all our heavy equipment operators are fully certified, licensed, and have extensive experience. They undergo regular safety training and equipment-specific certification updates."
    },
    {
      question: "What areas in Ontario do you serve for heavy equipment services?",
      answer: "We provide heavy equipment services throughout Ontario, including Toronto, Ottawa, Hamilton, London, Windsor, Kitchener-Waterloo, and surrounding municipalities."
    },
    {
      question: "Do you provide emergency heavy equipment services?",
      answer: "Yes, we offer 24/7 emergency heavy equipment services for urgent construction needs, disaster response, and time-sensitive projects throughout Ontario."
    },
    {
      question: "What safety measures do you follow for heavy equipment operations?",
      answer: "We strictly adhere to Ontario health and safety regulations, conduct pre-operation equipment inspections, provide comprehensive safety training, and maintain full insurance coverage for all operations."
    }
  ];

  const serviceData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Heavy Equipment Services Ontario",
    "description": "Professional heavy equipment rental, operation, and maintenance services across Ontario. Certified operators, modern fleet, and comprehensive project support.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Blue Knight Contracting",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Ontario",
        "addressCountry": "CA"
      },
      "telephone": "+1-647-760-8854"
    },
    "areaServed": "Ontario, Canada",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Heavy Equipment Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Equipment Rental",
            "description": "Short and long-term heavy equipment rental"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Certified Operators",
            "description": "Professional certified heavy equipment operators"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Equipment Maintenance",
            "description": "Comprehensive heavy equipment maintenance and repair"
          }
        }
      ]
    }
  };

  const heavyEquipmentServices = [
    {
      title: "Excavation Equipment",
      description: "Professional excavators, mini-excavators, and specialized digging equipment for all project sizes.",
      icon: Truck,
      services: [
        "Track Excavators (Various Sizes)",
        "Mini Excavators for Tight Spaces",
        "Long Reach Excavators",
        "Demolition Excavators",
        "Wheeled Excavators",
        "Specialized Attachments"
      ]
    },
    {
      title: "Earthmoving Equipment",
      description: "Bulldozers, graders, and compactors for site preparation and earthwork projects.",
      icon: Settings,
      services: [
        "Bulldozers (All Sizes)",
        "Motor Graders",
        "Soil Compactors",
        "Scrapers",
        "Backhoe Loaders",
        "Track Loaders"
      ]
    },
    {
      title: "Lifting & Crane Services",
      description: "Mobile cranes, tower cranes, and specialized lifting equipment with certified operators.",
      icon: Hammer,
      services: [
        "Mobile Cranes",
        "Tower Cranes",
        "Rough Terrain Cranes",
        "Boom Lifts",
        "Scissor Lifts",
        "Material Hoists"
      ]
    },
    {
      title: "Material Handling",
      description: "Loaders, forklifts, and material handling equipment for efficient project operations.",
      icon: Building,
      services: [
        "Wheel Loaders",
        "Skid Steer Loaders",
        "Telehandlers",
        "Forklifts",
        "Conveyor Systems",
        "Dump Trucks"
      ]
    },
    {
      title: "Equipment Rental",
      description: "Flexible rental options for all heavy equipment with competitive rates and maintenance included.",
      icon: ShoppingCart,
      services: [
        "Daily Rentals",
        "Weekly Rentals",
        "Monthly Rentals",
        "Long-term Leasing",
        "Rent-to-Own Options",
        "Fleet Management"
      ]
    },
    {
      title: "Maintenance & Repair",
      description: "Comprehensive maintenance, repair, and servicing for all types of heavy equipment.",
      icon: Cog,
      services: [
        "Preventive Maintenance",
        "Emergency Repairs",
        "Engine Overhauls",
        "Hydraulic System Service",
        "Parts Replacement",
        "Equipment Inspections"
      ]
    }
  ];

  const serviceAreas = [
    "Toronto", "Ottawa", "Hamilton", "London", "Windsor", "Kitchener-Waterloo",
    "Thunder Bay", "Sudbury", "Kingston", "Barrie", "Oshawa", "St. Catharines",
    "Cambridge", "Guelph", "Brantford", "Peterborough", "Sarnia", "Belleville"
  ];

  return (
    <>
      <SEOMeta 
        title="Heavy Equipment Ontario | Rental, Operators & Maintenance Services | Niagara Region, Hamilton and Toronto GTA"
        description="Professional heavy equipment services in Ontario. Equipment rental, certified operators, excavation & maintenance. Serving Toronto, Hamilton, Niagara & GTA construction sites."
        keywords="heavy equipment Ontario, equipment rental Toronto, excavator rental, crane rental Hamilton, certified operators Niagara, equipment maintenance GTA, construction equipment, Niagara Region heavy equipment, Hamilton heavy equipment, Toronto GTA heavy equipment"
        canonicalUrl="https://blueknightcontracting.ca/services/heavy-equipment"
      />
      <SchemaMarkup type="Service" data={serviceData} />
      <SchemaMarkup type="FAQ" data={faqData} />
      
      <Header />
      
      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary via-primary-dark to-secondary py-24 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <Badge variant="secondary" className="mb-4 bg-white/10 text-white border-white/20">
                  Professional Heavy Equipment Services
                </Badge>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                  Heavy Equipment Services Across Ontario
                </h1>
                <p className="text-xl lg:text-2xl mb-8 text-white/90 leading-relaxed">
                  Professional heavy equipment rental, certified operators, and comprehensive maintenance services. Modern fleet, competitive rates, and expert project support throughout Ontario.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                    <Link to="/contact">Get Free Quote</Link>
                  </Button>
                  <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                    <a href="tel:+16477608854">Emergency Service: (647) 760-8854</a>
                  </Button>
                </div>
                <div className="flex flex-wrap gap-6 text-white/80">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    <span>Fully Licensed & Insured</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    <span>Certified Operators</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    <span>24/7 Emergency Service</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={heavyEquipmentSite} 
                  alt="Professional heavy equipment services in Ontario" 
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20 bg-gradient-to-b from-background to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Our Heavy Equipment Services</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Comprehensive Heavy Equipment Solutions
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                From excavation to material handling, we provide the complete range of heavy equipment services with modern machinery and expert operators.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {heavyEquipmentServices.map((service, index) => (
                <Card key={index} className="h-full hover:shadow-lg transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <service.icon className="h-8 w-8 text-primary" />
                      </div>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                    </div>
                    <CardDescription className="text-base">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.services.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Projects */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Recent Projects</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Heavy Equipment Projects Across Ontario
              </h2>
              <p className="text-xl text-muted-foreground">
                See our heavy equipment in action on various construction and infrastructure projects.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img 
                    src={heavyEquipmentOperator} 
                    alt="Professional heavy equipment operation" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Professional Operations</h3>
                    <p className="text-sm text-white/80">Certified operator services</p>
                  </div>
                </div>
              </Card>
              
              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img 
                    src={heavyEquipmentRental} 
                    alt="Heavy equipment rental fleet" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Equipment Rental</h3>
                    <p className="text-sm text-white/80">Modern fleet available</p>
                  </div>
                </div>
              </Card>
              
              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img 
                    src={heavyEquipmentDemolition} 
                    alt="Professional demolition services" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Demolition Services</h3>
                    <p className="text-sm text-white/80">Safe and efficient</p>
                  </div>
                </div>
              </Card>
              
              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img 
                    src={heavyEquipmentMaintenance} 
                    alt="Heavy equipment maintenance" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Maintenance Services</h3>
                    <p className="text-sm text-white/80">Keep equipment running</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Emergency Services CTA */}
        <section className="py-16 bg-destructive text-destructive-foreground">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Clock className="h-8 w-8" />
                <h2 className="text-3xl font-bold">24/7 Emergency Heavy Equipment Services</h2>
              </div>
              <p className="text-xl mb-8 opacity-90">
                Urgent project needs? Equipment breakdown? We provide immediate heavy equipment support across Ontario.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                  <a href="tel:+16477608854">Call Emergency Line: (647) 760-8854</a>
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                  <Link to="/contact">Request Emergency Service</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-gradient-to-b from-secondary/5 to-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Why Choose Blue Knight Contracting</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Ontario's Trusted Heavy Equipment Specialists
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Years of experience, modern equipment fleet, and commitment to safety make us the preferred choice for heavy equipment services.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Award className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Certified Excellence</h3>
                <p className="text-muted-foreground">All operators certified and equipment regularly inspected for safety and performance.</p>
              </Card>
              
              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Truck className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Modern Fleet</h3>
                <p className="text-muted-foreground">Latest heavy equipment models with advanced features and reliable performance.</p>
              </Card>
              
              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Clock className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">24/7 Availability</h3>
                <p className="text-muted-foreground">Round-the-clock service for emergency situations and urgent project needs.</p>
              </Card>
              
              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Fully Insured</h3>
                <p className="text-muted-foreground">Comprehensive insurance coverage and strict safety protocols for all operations.</p>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Service Coverage</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Heavy Equipment Services Across Ontario
              </h2>
              <p className="text-xl text-muted-foreground">
                We provide comprehensive heavy equipment services throughout Ontario's major cities and surrounding areas.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {serviceAreas.map((area, index) => (
                  <Card key={index} className="text-center p-4 hover:shadow-md transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                    <div className="flex items-center justify-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span className="font-medium">{area}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gradient-to-b from-background to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Frequently Asked Questions</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Heavy Equipment Services FAQ
              </h2>
              <p className="text-xl text-muted-foreground">
                Common questions about our heavy equipment rental, operation, and maintenance services.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto grid gap-6">
              {faqData.map((faq, index) => (
                <Card key={index} className="border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-left text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-br from-primary via-primary-dark to-secondary text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Ready to Start Your Heavy Equipment Project?
              </h2>
              <p className="text-xl mb-8 text-white/90">
                Get professional heavy equipment services with certified operators and modern machinery. Contact us for a free consultation and competitive quote.
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Phone className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Call Us</h3>
                  <p className="text-white/80">
                    <a href="tel:+16477608854" className="hover:text-white transition-colors">
                      (647) 760-8854
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Mail className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Email Us</h3>
                  <p className="text-white/80">
                    <a href="mailto:info@blueknightcontracting.ca" className="hover:text-white transition-colors">
                      info@blueknightcontracting.ca
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <MapPin className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Service Area</h3>
                  <p className="text-white/80">All of Ontario</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                  <Link to="/contact">Get Free Equipment Quote</Link>
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                  <Link to="/gallery">View Our Equipment</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default HeavyEquipmentServices;